<template>
  <div>
    <div
      v-if="isRefreshing && !currentSession"
      class="rounded-lg bg-white p-6 shadow dark:bg-gray-800 sm:p-8"
    >
      <LoadingSpinner
        size="lg"
        color="primary"
        :text="t('app.views.cashRegister.loadingSession') || 'Cargando sesión...'"
      />
    </div>

    <div
      v-else-if="currentSession"
      class="rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/55 sm:p-6"
    >
      <div class="mb-4 flex flex-wrap items-center gap-2.5 text-xs sm:text-sm">
        <span class="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
          {{ t('app.views.cashRegister.sessionNumber') || 'Session' }} #{{ currentSession.session_number }}
        </span>
        <span class="rounded-full border border-slate-300 bg-slate-50 px-3 py-1 text-slate-600 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-300">
          {{ t('app.views.cashRegister.openedAt') || 'Opened' }}: {{ sessionDuration }}
        </span>
      </div>

      <div class="mb-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        <BaseButton
          variant="secondary"
          outlined
          size="sm"
          class="w-full justify-center border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          @click="emit('open-expense')"
        >
          <template #icon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 sm:h-5 sm:w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                clip-rule="evenodd"
              />
            </svg>
          </template>
          {{ t('app.views.cashRegister.addExpense') || 'Add Expense' }}
        </BaseButton>

        <BaseButton
          variant="secondary"
          outlined
          size="sm"
          class="w-full justify-center border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          @click="emit('open-cut')"
        >
          <template #icon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 sm:h-5 sm:w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fill-rule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clip-rule="evenodd"
              />
            </svg>
          </template>
          {{ t('app.views.cashRegister.cut') || 'Cut' }}
        </BaseButton>

        <BaseButton
          variant="danger"
          outlined
          size="sm"
          class="w-full justify-center"
          @click="emit('open-close')"
        >
          <template #icon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 sm:h-5 sm:w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5 8a3 3 0 116 0v1h1a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4a2 2 0 012-2h1V8zm2-1a1 1 0 112 0v1H7V7z"
                clip-rule="evenodd"
              />
            </svg>
          </template>
          {{ t('app.views.cashRegister.close') || 'Close Session' }}
        </BaseButton>
      </div>

      <SessionStatsCards
        :initial-balance="currentSession.initial_balance || 0"
        :current-balance="currentBalance"
        :sales-amount="sessionSales"
        :sales-count="salesCount"
        :total-expenses="sessionExpenses"
        :expenses-count="expensesCount"
        :is-refreshing="isRefreshing"
      />
    </div>

    <div
      v-else
      class="text-center"
    >
      <div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800 sm:p-8">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="mx-auto mb-4 h-12 w-12 text-gray-400 sm:h-16 sm:w-16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 class="mb-2 text-base font-medium text-gray-900 dark:text-gray-100 sm:text-lg">
          {{ t('app.views.cashRegister.noActiveSession') || 'No Active Session' }}
        </h3>
        <p class="mb-4 text-sm text-gray-600 dark:text-gray-400 sm:text-base">
          {{ t('app.views.cashRegister.openSessionToStart') || 'Open a session to start managing cash register operations.' }}
        </p>
        <BaseButton
          variant="primary"
          size="lg"
          full-width
          class="sm:w-auto"
          @click="emit('open-open')"
        >
          {{ t('app.views.cashRegister.openSession') || 'Open Session' }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import BaseButton from '@/components/ui/BaseButton.vue';
import SessionStatsCards from '@/components/cashRegister/SessionStatsCards.vue';
import type { CashRegisterSession } from '@/utils/cashRegisterHelpers';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

defineProps<{
  currentSession: CashRegisterSession | null;
  isRefreshing: boolean;
  sessionDuration: string;
  currentBalance: number;
  sessionSales: number;
  salesCount: number;
  sessionExpenses: number;
  expensesCount: number;
}>();

const emit = defineEmits<{
  (e: 'open-open'): void;
  (e: 'open-close'): void;
  (e: 'open-expense'): void;
  (e: 'open-cut'): void;
}>();

const { t } = useI18n();
</script>
